// Tab interface for type safety
export interface TabItem {
  id: string | number;
  title: string;
  icon?: string;
  disabled?: boolean;
  content?: string;
  template?: any; // For custom template content
  // When true, `title` is a REF_TRADUCTION key rendered via [tmwTranslate] (shows the inline edit icon)
  // instead of a plain, already-resolved display string.
  translate?: boolean;
}

// Component TypeScript
import { Component, Input, Output, EventEmitter, ContentChildren, QueryList, TemplateRef, AfterContentInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbNavModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { TmwTranslateDirective } from '@shared/directives/translate.directive';

@Component({
  standalone: true,
  selector: 'tmw-reusable-tabs',
  templateUrl: './reusable-tabs.component.html',
  styleUrls: ['./reusable-tabs.component.scss'],
  imports: [CommonModule, NgbNavModule, TranslateModule, TmwTranslateDirective],
})
export class TmwTabsComponent implements AfterContentInit {
  @Input() tabs: TabItem[] = [];
  @Input() activeTabId: string | number = 1;
  @Input() showIcons: boolean = false;
  @Input() tabsClass: string = 'nav-border-top nav-border-top-primary';
  @Input() tabContentClass: string = '';
  @Input() allowDynamicTabs: boolean = false;
  @Input() maxTabs: number = 10;

  @Output() activeTabChange = new EventEmitter<string | number>();
  @Output() tabAdded = new EventEmitter<TabItem>();
  @Output() tabRemoved = new EventEmitter<string | number>();
  @Output() tabClick = new EventEmitter<TabItem>();

  @ContentChildren('tabContent') tabContentTemplates!: QueryList<TemplateRef<any>>;

  ngAfterContentInit() {
    // Map templates to tabs if provided
    if (this.tabContentTemplates.length > 0) {
      const templates = this.tabContentTemplates.toArray();
      this.tabs.forEach((tab, index) => {
        // Un seul template (ngSwitch sur tab.id, ex. fiche utilisateur en consultation) : partage par tous les onglets
        const template = templates[index] ?? (templates.length === 1 ? templates[0] : undefined);
        if (template) {
          tab.template = template;
        }
      });
    }
  }

  onTabChange(tabId: string | number) {
    this.activeTabId = tabId;
    this.activeTabChange.emit(tabId);

    const selectedTab = this.tabs.find((tab) => tab.id === tabId);
    if (selectedTab) {
      this.tabClick.emit(selectedTab);
    }
  }

  addTab() {
    if (this.tabs.length >= this.maxTabs) return;

    const newTab: TabItem = {
      id: `tab_${Date.now()}`,
      title: `Nouvel Onglet ${this.tabs.length + 1}`,
      content: `Contenu du nouvel onglet ${this.tabs.length + 1}`,
    };

    this.tabs.push(newTab);
    this.activeTabId = newTab.id;
    this.tabAdded.emit(newTab);
    this.activeTabChange.emit(newTab.id);
  }

  removeTab(tabId: string | number, event: Event) {
    event.stopPropagation();

    const index = this.tabs.findIndex((tab) => tab.id === tabId);
    if (index === -1 || this.tabs.length <= 1) return;

    this.tabs.splice(index, 1);
    this.tabRemoved.emit(tabId);

    // If the removed tab was active, activate another tab
    if (this.activeTabId === tabId) {
      const newActiveTab = this.tabs[Math.min(index, this.tabs.length - 1)];
      this.activeTabId = newActiveTab.id;
      this.activeTabChange.emit(this.activeTabId);
    }
  }

  getTabById(id: string | number): TabItem | undefined {
    return this.tabs.find((tab) => tab.id === id);
  }

  trackByTabId(index: number, tab: TabItem): string | number {
    return tab.id;
  }
}
